import { useCallback, useEffect, useState } from 'react';
import {
  CreditCard, Download, FileText, IndianRupee, CheckCircle2, RefreshCw, Receipt,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  listPayments, paymentsSummary, exportPayments, downloadReceipt,
  type PaymentRow, type PaymentSummary, type PaymentFilterParams, type PaymentStatusFilter,
} from '../services/admin_api';
import { extractErrorMessage } from '@/services/api';

// Payments page: read-only reporting over the purchases ledger. Filter by
// date range + status, download the GST receipt for any captured payment,
// and export the filtered set to XLSX. Nothing here mutates payment state.
const PAGE_SIZE = 15;

const STATUS_OPTIONS: { value: PaymentStatusFilter; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'captured', label: 'Captured' },
  { value: 'failed', label: 'Failed' },
  { value: 'refunded', label: 'Refunded' },
];

export function Payments() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [status, setStatus] = useState<PaymentStatusFilter>('all');

  const [rows, setRows] = useState<PaymentRow[]>([]);
  const [summary, setSummary] = useState<PaymentSummary | null>(null);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [receiptBusy, setReceiptBusy] = useState<string | null>(null);

  // Snapshot the current filter for API calls. Date range drives the
  // summary cards; status additionally narrows the table + export.
  const filter = useCallback(
    (): PaymentFilterParams => ({ from: from || undefined, to: to || undefined, status }),
    [from, to, status],
  );

  const refresh = useCallback(async (p = 0) => {
    setLoading(true);
    try {
      const [list, sum] = await Promise.all([
        listPayments(filter(), PAGE_SIZE + 1, p * PAGE_SIZE),
        // Summary ignores the status filter server-side (date range only).
        paymentsSummary({ from: from || undefined, to: to || undefined }),
      ]);
      setRows(list);
      setSummary(sum);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Failed to load payments'));
    } finally {
      setLoading(false);
    }
  }, [filter, from, to]);

  // Re-query whenever a filter changes; reset to the first page.
  useEffect(() => {
    setPage(0);
    refresh(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, status]);

  useEffect(() => {
    refresh(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const onExport = async () => {
    setExporting(true);
    try {
      const blob = await exportPayments(filter());
      triggerDownload(blob, `payments-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const onReceipt = async (row: PaymentRow) => {
    setReceiptBusy(row.id);
    try {
      const { blob, filename } = await downloadReceipt(row.id);
      triggerDownload(blob, filename);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Receipt download failed'));
    } finally {
      setReceiptBusy(null);
    }
  };

  const clearFilters = () => {
    setFrom('');
    setTo('');
    setStatus('all');
  };

  const hasMore = rows.length > PAGE_SIZE;
  const visible = hasMore ? rows.slice(0, PAGE_SIZE) : rows;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <CreditCard className="h-6 w-6 text-primary" /> Payments
          </h1>
          <p className="text-sm text-text-secondary">
            Live Razorpay payments only. Download GST receipts and export by date range.
          </p>
        </div>
        <button className="btn-ghost" disabled={exporting} onClick={onExport}>
          <Download className="mr-1 h-4 w-4" /> {exporting ? 'Exporting…' : 'Export'}
        </button>
      </div>

      {/* Live-only stat cards: captured count, revenue, and average order
          value — all over the selected date range. No test data, no pending. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard
          icon={<CheckCircle2 className="h-4 w-4" />}
          label="Live payments"
          value={summary ? summary.captured_count.toLocaleString('en-IN') : '—'}
          accent="emerald"
        />
        <StatCard
          icon={<IndianRupee className="h-4 w-4" />}
          label="Live revenue"
          value={summary ? `₹${summary.revenue_inr.toLocaleString('en-IN')}` : '—'}
          accent="emerald"
        />
        <StatCard
          icon={<Receipt className="h-4 w-4" />}
          label="Avg order value"
          value={summary && summary.captured_count > 0
            ? `₹${Math.round(summary.revenue_inr / summary.captured_count).toLocaleString('en-IN')}`
            : '—'}
        />
      </div>

      {/* Filter bar */}
      <div className="card flex flex-wrap items-end gap-3 p-3">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-text-secondary">From</span>
          <input type="date" className="input" value={from} max={to || undefined}
            onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-text-secondary">To</span>
          <input type="date" className="input" value={to} min={from || undefined}
            onChange={(e) => setTo(e.target.value)} />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-text-secondary">Status</span>
          <select className="input" value={status}
            onChange={(e) => setStatus(e.target.value as PaymentStatusFilter)}>
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        {(from || to || status !== 'all') && (
          <button className="btn-ghost text-xs" onClick={clearFilters}>
            <RefreshCw className="mr-1 h-3.5 w-3.5" /> Clear
          </button>
        )}
        <div className="ml-auto text-xs text-text-secondary">
          Export respects the filters above.
        </div>
      </div>

      {/* Payments table */}
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-black/5 text-left text-xs uppercase text-text-secondary">
            <tr>
              <th className="px-3 py-2 font-medium">Date</th>
              <th className="px-3 py-2 font-medium">Email</th>
              <th className="px-3 py-2 font-medium">Plan</th>
              <th className="px-3 py-2 font-medium text-right">Amount</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium text-right">Receipt</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="py-6 text-center text-xs text-text-secondary">Loading…</td></tr>
            ) : visible.length === 0 ? (
              <tr><td colSpan={6} className="py-6 text-center text-xs text-text-secondary">
                No payments match the current filters.
              </td></tr>
            ) : visible.map((p) => (
              <tr key={p.id} className="border-t border-border/50">
                <td className="px-3 py-2 text-xs text-text-secondary whitespace-nowrap">
                  {shortDateTime(p.created_at)}
                </td>
                <td className="px-3 py-2">{p.email}</td>
                <td className="px-3 py-2">{p.pack_name}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  ₹{p.amount_inr.toLocaleString('en-IN')}
                </td>
                <td className="px-3 py-2"><StatusBadge status={p.status} /></td>
                <td className="px-3 py-2 text-right">
                  {p.has_receipt ? (
                    <button
                      className="btn-ghost text-xs"
                      disabled={receiptBusy === p.id}
                      onClick={() => onReceipt(p)}
                      title={p.invoice_number ? `Invoice ${p.invoice_number}` : 'Download receipt'}
                    >
                      <FileText className="mr-1 h-3.5 w-3.5" />
                      {receiptBusy === p.id ? '…' : 'Receipt'}
                    </button>
                  ) : (
                    <span className="text-xs text-text-secondary">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(page > 0 || hasMore) && (
        <div className="flex items-center justify-between text-sm">
          <button className="btn-ghost" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
            ← Previous
          </button>
          <span className="text-xs text-text-secondary">Page {page + 1}</span>
          <button className="btn-ghost" disabled={!hasMore} onClick={() => setPage((p) => p + 1)}>
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: PaymentRow['status'] }) {
  const map: Record<PaymentRow['status'], { cls: string; label: string }> = {
    paid: { cls: 'bg-emerald-50 text-emerald-700', label: 'Captured' },
    created: { cls: 'bg-amber-50 text-amber-700', label: 'Pending' },
    failed: { cls: 'bg-red-50 text-red-700', label: 'Failed' },
    refunded: { cls: 'bg-black/5 text-text-secondary', label: 'Refunded' },
  };
  const s = map[status];
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wide ${s.cls}`}>
      {s.label}
    </span>
  );
}

function StatCard({
  icon, label, value, accent,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  accent?: 'emerald' | 'amber';
}) {
  const accentMap: Record<string, string> = {
    emerald: 'text-emerald-600 bg-emerald-50',
    amber: 'text-amber-600 bg-amber-50',
  };
  const accentCls = accent ? accentMap[accent] : 'text-text-secondary bg-black/5';
  return (
    <div className="card flex items-center gap-3 p-3">
      <div className={`flex h-9 w-9 items-center justify-center rounded-md ${accentCls}`}>
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-wider text-text-secondary">{label}</div>
        <div className="text-xl font-semibold tabular-nums">{value}</div>
      </div>
    </div>
  );
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function shortDateTime(iso: string): string {
  try {
    // Render in IST so the admin sees the same clock the finance range uses.
    return new Date(iso).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
